import React from 'react';

/**
 * FeedbackInput — reusable textarea for free-form user feedback.
 *
 * Used in two contexts:
 *   1. Inline in a form (ProductForm, QuickContentFormV2) so the user can
 *      provide additional instructions at generation time.
 *   2. Inside a modal (RegenerateWithFeedbackModal, RegenerateCampaignFeedbackModal)
 *      so the user can describe fixes when regenerating an ad or campaign.
 *
 * Props:
 *   value        — controlled text value
 *   onChange     — (newValue) => void
 *   label        — section label (defaults to "Additional instructions")
 *   placeholder  — placeholder text with suggested examples
 *   maxLength    — character cap (default 1000)
 *   rows         — textarea height (default 4)
 *   disabled     — disables the textarea
 *   testIdBase   — prefix for data-testid attrs (default 'feedback-input')
 *   helperText   — bottom hint, defaults to a generic helper
 *   onKeyDown    — passthrough for keyboard shortcuts (e.g. Cmd+Enter to submit)
 */
const DEFAULT_PLACEHOLDER =
  'e.g. Fix the spelling of "AdElevate". Make the tone more urgent. Remove the discount mention. Highlight free shipping.';

export const FeedbackInput = ({
  value,
  onChange,
  label = 'Additional instructions',
  optionalText = '(optional)',
  placeholder = DEFAULT_PLACEHOLDER,
  maxLength = 1000,
  rows = 4,
  disabled = false,
  testIdBase = 'feedback-input',
  helperText,
  onKeyDown,
}) => {
  const len = (value || '').length;
  const overThreshold = len > maxLength * 0.9;
  const defaultHelper = 'Leave blank to use the original settings.';

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={`${testIdBase}-textarea`}
          className="block text-sm font-medium text-slate-800 dark:text-slate-200"
        >
          {label}{' '}
          {optionalText && (
            <span className="text-slate-400 font-normal">{optionalText}</span>
          )}
        </label>
      )}
      <textarea
        id={`${testIdBase}-textarea`}
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
        className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none disabled:opacity-60"
        data-testid={`${testIdBase}-textarea`}
      />
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {helperText || defaultHelper}
        </p>
        <p
          className={`text-xs ${overThreshold ? 'text-amber-600' : 'text-slate-400'}`}
          data-testid={`${testIdBase}-counter`}
        >
          {len}/{maxLength}
        </p>
      </div>
    </div>
  );
};

export default FeedbackInput;
